import { useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { CopyWrapper } from "./copy-wrapper";

interface StackFrame {
  raw: string;
  file?: string;
  line?: number;
  col?: number;
  fn?: string;
  isLibrary: boolean;
}

function parseStackTrace(error: string): { message: string; frames: StackFrame[] } {
  const lines = error.split("\n");
  let message = "";
  const frames: StackFrame[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Match common stack trace patterns:
    // "at functionName (file:line:col)"
    // "at file:line:col"
    // "File "path", line N, in function"  (Python)
    const jsMatch = trimmed.match(/^at\s+(?:(.+?)\s+)?\(?(.+?):(\d+):(\d+)\)?$/);
    if (jsMatch) {
      frames.push({
        raw: trimmed,
        fn: jsMatch[1] || undefined,
        file: jsMatch[2],
        line: parseInt(jsMatch[3]),
        col: parseInt(jsMatch[4]),
        isLibrary: jsMatch[2].includes("node_modules") || jsMatch[2].includes("internal/"),
      });
      continue;
    }

    const pyMatch = trimmed.match(/^File "(.+?)", line (\d+)(?:, in (.+))?$/);
    if (pyMatch) {
      frames.push({
        raw: trimmed,
        file: pyMatch[1],
        line: parseInt(pyMatch[2]),
        fn: pyMatch[3] || undefined,
        isLibrary: pyMatch[1].includes("site-packages") || pyMatch[1].includes("/lib/python"),
      });
      continue;
    }

    if (frames.length === 0) {
      message += (message ? "\n" : "") + line;
    }
  }

  return { message: message.trim(), frames };
}

interface StackTraceBlockProps {
  error: string;
}

export function StackTraceBlock({ error }: StackTraceBlockProps) {
  const [showLibrary, setShowLibrary] = useState(false);
  const { message, frames } = parseStackTrace(error);

  const appFrames = frames.filter((f) => !f.isLibrary);
  const libraryFrames = frames.filter((f) => f.isLibrary);
  const displayFrames = showLibrary ? frames : appFrames.length > 0 ? appFrames : frames;

  return (
    <CopyWrapper copyText={error} label="Copy stack trace" className="my-2">
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 overflow-hidden">
        {/* Error message header */}
        <div className="flex items-start gap-2 border-b border-red-500/20 bg-red-500/10 px-3 py-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <pre className="whitespace-pre-wrap font-mono text-xs text-red-700 dark:text-red-400">
            {message || "Error"}
          </pre>
        </div>

        {/* Stack frames */}
        {frames.length > 0 && (
          <div className="p-2">
            <div className="space-y-0.5">
              {displayFrames.map((frame, i) => (
                <div
                  key={i}
                  className={`rounded px-2 py-1 font-mono text-xs ${
                    frame.isLibrary
                      ? "text-muted-foreground/60"
                      : "bg-background/60 text-foreground"
                  }`}
                >
                  {frame.fn && (
                    <span className="font-medium text-blue-600 dark:text-blue-400">
                      {frame.fn}
                    </span>
                  )}
                  {frame.file && (
                    <span className="ml-1 text-muted-foreground">
                      {frame.fn ? " at " : ""}
                      <span className="text-foreground/70">{frame.file}</span>
                      {frame.line && (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          :{frame.line}
                          {frame.col && `:${frame.col}`}
                        </span>
                      )}
                    </span>
                  )}
                  {!frame.fn && !frame.file && (
                    <span className="text-muted-foreground">{frame.raw}</span>
                  )}
                </div>
              ))}
            </div>

            {libraryFrames.length > 0 && appFrames.length > 0 && (
              <button
                type="button"
                className="mt-1.5 flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
                onClick={() => setShowLibrary((p) => !p)}
              >
                {showLibrary ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                {showLibrary ? "Hide" : "Show"} {libraryFrames.length} library frame{libraryFrames.length > 1 ? "s" : ""}
              </button>
            )}
          </div>
        )}
      </div>
    </CopyWrapper>
  );
}
